import React, { useEffect, useMemo, useRef, useState } from 'react';

export type LightboxItem = { url: string; name: string };

type Props = {
  open: boolean;
  items: LightboxItem[];
  index: number;
  onClose: () => void;
  onChangeIndex: (idx: number) => void;
};

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

const ImageLightbox: React.FC<Props> = ({ open, items, index, onClose, onChangeIndex }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [baseScale, setBaseScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{x:number;y:number;tx:number;ty:number}|null>(null);

  const item = items[index];

  // Compute a "fit to screen" base scale whenever image or viewport changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const compute = async () => {
      const el = containerRef.current;
      if (!el) return;
      const { clientWidth: cw, clientHeight: ch } = el;
      // Preload to get natural size
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = item?.url || '';
      });
      // Create a temp image to read natural sizes
      const tmp = new Image();
      tmp.src = item?.url || '';
      const iw = tmp.naturalWidth || 1;
      const ih = tmp.naturalHeight || 1;
      const fit = Math.min(cw / iw, ch / ih, 1);
      if (!cancelled) {
        setBaseScale(fit);
        setScale(fit);
        setTx(0);
        setTy(0);
      }
    };
    compute();
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    return () => { cancelled = true; window.removeEventListener('resize', onResize); };
  }, [open, index, item]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onChangeIndex((index + 1) % items.length);
      if (e.key === 'ArrowLeft') onChangeIndex((index - 1 + items.length) % items.length);
      if (e.key === '+') setScale(s => clamp(s + 0.2, 0.5, 4));
      if (e.key === '-') setScale(s => clamp(s - 0.2, 0.5, 4));
      if (e.key === '0') { setScale(1); setTx(0); setTy(0); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, items.length, onClose, onChangeIndex]);

  const startDrag = (e: React.MouseEvent) => {
    if (scale <= 1) return;
    setDragging(true);
    dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
  };
  const onDrag = (e: React.MouseEvent) => {
    if (!dragging || !dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setTx(dragRef.current.tx + dx);
    setTy(dragRef.current.ty + dy);
  };
  const endDrag = () => { setDragging(false); dragRef.current = null; };

  const zoom = (delta: number) => setScale(s => clamp(s + delta, baseScale * 0.2, baseScale * 8));
  const reset = () => { setScale(baseScale); setTx(0); setTy(0); };
  const prev = () => onChangeIndex((index - 1 + items.length) % items.length);
  const next = () => onChangeIndex((index + 1) % items.length);

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 text-white select-none">
      <div className="absolute inset-0" ref={containerRef}>
        <div className="absolute top-4 left-4 flex items-center gap-2 z-20">
          <button className="rounded-md bg-white/10 hover:bg-white/20 px-3 py-2" onClick={onClose}>Close</button>
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
          <button className="rounded-md bg-white/10 hover:bg-white/20 px-3 py-2" onClick={() => zoom(-0.2)}>-</button>
          <button className="rounded-md bg-white/10 hover:bg-white/20 px-3 py-2" onClick={() => zoom(+0.2)}>+</button>
          <button className="rounded-md bg-white/10 hover:bg-white/20 px-3 py-2" onClick={reset}>Reset</button>
        </div>
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden z-10">
          <img
            src={item.url}
            alt={item.name}
            className="max-w-none"
            style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})`, transition: dragging ? 'none' : 'transform 120ms ease', transformOrigin: 'center center' }}
            draggable={false}
            onMouseDown={startDrag}
            onMouseMove={onDrag}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
          />
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/80 z-20">
          {item.name}
        </div>
        {items.length > 1 && (
          <>
            <button className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 w-10 h-10 flex items-center justify-center z-20" onClick={prev} aria-label="Previous">
              ‹
            </button>
            <button className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 w-10 h-10 flex items-center justify-center z-20" onClick={next} aria-label="Next">
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ImageLightbox;
